import { Head } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { SiteLayout } from '@/components/site-layout'

type Props = Record<string, never>

type AwardCategory = {
  id: number
  title: string
  description: string
  criteria: string[]
}

type TheLeArticle = {
  number: number
  title: string
  body: string
  formula?: string
  points?: string[]
  footer?: string
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="mt-3 space-y-2 text-[#5b7788] text-sm">
      {items.map((item) => (
        <li key={item} className="flex gap-3">
          <span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-[#FCC632]" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  )
}

export default function DieuLeIndex(_props: Props) {
  const { t, i18n } = useTranslation()

  const eligibilityPoints = t('eligibility.points', { returnObjects: true }) as string[]
  const awardCategories = t('awards.items', { returnObjects: true }) as AwardCategory[]
  const theLeArticles = t('theLe.articles', { returnObjects: true }) as TheLeArticle[]

  const handleLanguageChange = (language: string) => {
    void i18n.changeLanguage(language)
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('language', language)
    }
  }

  return (
    <SiteLayout>
      <Head title={t('pageTitle')} />
      <section className="mx-auto max-w-4xl animate-fade-in-up px-4 pt-8 text-center sm:px-6 sm:pt-10">
        <p className="text-[#5b7788] text-[10px] uppercase tracking-[0.35em] sm:text-[11px]">
          {t('hero.eyebrow')}
        </p>
        <h1 className="mt-3 font-display font-semibold text-3xl tracking-tight sm:mt-4 sm:text-4xl md:text-5xl">
          {t('hero.title')}
        </h1>
        <p className="mt-3 text-[#5b7788] text-sm sm:mt-4 md:text-base">{t('hero.subtitle')}</p>
        <div className="mt-6 flex items-center justify-center gap-2 text-[#5b7788] text-xs uppercase tracking-[0.2em]">
          <span>{t('languageLabel')}</span>
          <div className="flex items-center rounded-full border border-[#042133]/15 bg-white/80 p-1">
            {[
              { code: 'vi', label: 'VI' },
              { code: 'en', label: 'EN' },
            ].map((language) => (
              <button
                key={language.code}
                type="button"
                onClick={() => handleLanguageChange(language.code)}
                className={
                  i18n.language === language.code
                    ? 'cursor-pointer rounded-full bg-[#042133] px-3 py-1 font-semibold text-[10px] text-white'
                    : 'cursor-pointer rounded-full px-3 py-1 font-semibold text-[#5b7788] text-[10px] hover:text-[#042133]'
                }
              >
                {language.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto mt-10 max-w-6xl px-4 pb-16 sm:mt-12 sm:px-6 sm:pb-20">
        <div className="space-y-6">
          <div className="rounded-3xl border border-[#042133]/10 bg-white/85 p-6 shadow-[0_30px_90px_-55px_rgba(4,33,51,0.45)] sm:p-8">
            <p className="text-[#5b7788] text-[11px] uppercase tracking-[0.3em]">
              {t('eligibility.title')}
            </p>
            <h2 className="mt-2 font-display font-semibold text-2xl sm:text-3xl">
              {t('eligibility.subtitle')}
            </h2>
            <div className="mt-6">
              <p className="text-[#5b7788] text-sm">{t('eligibility.description')}</p>
              <p className="mt-4 font-semibold text-[#042133] text-sm">
                {t('eligibility.requirementsTitle')}
              </p>
              <BulletList items={eligibilityPoints} />
              <p className="mt-4 text-[#5b7788] text-sm">{t('eligibility.ineligibleNotice')}</p>
              <p className="mt-4 text-[#5b7788] text-sm">{t('eligibility.verificationNotice')}</p>
            </div>
          </div>

          <div className="rounded-3xl border border-[#042133]/10 bg-white/85 p-6 shadow-[0_30px_90px_-55px_rgba(4,33,51,0.45)] sm:p-8">
            <p className="text-[#5b7788] text-[11px] uppercase tracking-[0.3em]">
              {t('theLe.title')}
            </p>
            <h2 className="mt-2 font-display font-semibold text-2xl sm:text-3xl">
              {t('theLe.subtitle')}
            </h2>
            <div className="mt-6 space-y-6">
              {theLeArticles.map((article) => (
                <div key={article.number} className="rounded-2xl border border-[#042133]/10 bg-white/80 p-5">
                  <div className="flex items-start gap-3">
                    <span className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-[#042133] font-semibold text-white text-xs">
                      {article.number}
                    </span>
                    <p className="font-semibold text-[#042133] text-sm leading-7">{article.title}</p>
                  </div>
                  <p className="mt-3 text-[#5b7788] text-sm">{article.body}</p>
                  {article.points && article.points.length > 0 && (
                    <BulletList items={article.points} />
                  )}
                  {article.formula && (
                    <div className="mt-3 rounded-xl bg-[#042133]/5 px-4 py-3">
                      {article.formula.split('\n').map((line) => (
                        <p key={line} className="font-mono text-[#042133] text-xs leading-relaxed">
                          {line}
                        </p>
                      ))}
                    </div>
                  )}
                  {article.footer && (
                    <p className="mt-3 font-semibold text-[#042133] text-sm">{article.footer}</p>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-3xl border border-[#042133]/10 bg-white/85 p-6 shadow-[0_30px_90px_-55px_rgba(4,33,51,0.45)] sm:p-8">
            <p className="text-[#5b7788] text-[11px] uppercase tracking-[0.3em]">
              {t('awards.title')}
            </p>
            <h2 className="mt-2 font-display font-semibold text-2xl sm:text-3xl">
              {t('awards.subtitle')}
            </h2>
            <p className="mt-3 text-[#5b7788] text-sm">{t('awards.groupLabel')}</p>
            <div className="mt-6 space-y-5">
              {awardCategories.map((category) => (
                <div
                  key={category.id}
                  className="rounded-2xl border border-[#042133]/10 bg-white/80 p-5"
                >
                  <p className="text-[#5b7788] text-xs uppercase tracking-[0.25em]">
                    {t('awards.itemLabel')} {category.id}
                  </p>
                  <h3 className="mt-2 font-semibold text-[#042133] text-lg">{category.title}</h3>
                  <p className="mt-2 text-[#5b7788] text-sm">{category.description}</p>
                  {category.criteria.length > 0 && (
                    <div className="mt-4">
                      <p className="font-semibold text-[#042133] text-sm">
                        {t('awards.criteriaLabel')}
                      </p>
                      <BulletList items={category.criteria} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  )
}
